import type {
  AbstractPartDefinition,
  DragConstraint,
} from "@project/emerge-types";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";

/** Min/max margin (mm) along one connector's edge drag segment — same semantics as {@link MarginAxisRange} in addableMarginDragRange. */
export interface ConnectorMarginAxisRange {
  min: number;
  max: number;
}

function normalizeAlignment(value: unknown): Alignment {
  if (value === Alignment.CENTER || value === 1 || value === "CENTER") {
    return Alignment.CENTER;
  }
  if (value === Alignment.END || value === 2 || value === "END") {
    return Alignment.END;
  }
  if (value === Alignment.START || value === 0 || value === "START") {
    return Alignment.START;
  }
  if (
    value === Alignment.START ||
    value === Alignment.CENTER ||
    value === Alignment.END
  ) {
    return value;
  }
  return Alignment.START;
}

export interface ConnectorMarginParams {
  originalEdgeStart: THREE.Vector3;
  originalEdgeEnd: THREE.Vector3;
  originalEdgeLength: number;
  edgeDir: THREE.Vector3;
  count: number;
  n: number;
  padding: number;
  alignment: unknown;
  mirrored: boolean;
}

export type ConnectorMarginDragOption = Pick<
  ConnectorSetInfos,
  "alignment" | "count" | "partSelection"
>;

/**
 * Same margin math as `ProfileBuildStep.updateSettingsFromConnectorDrag`
 * (placement point on the reference edge, before grid snap / clamp).
 */
export function marginFromConnectorPlacement(
  placementPoint: THREE.Vector3,
  params: ConnectorMarginParams,
): number | null {
  const {
    originalEdgeStart,
    originalEdgeEnd,
    originalEdgeLength,
    edgeDir,
    count,
    n,
    padding,
    alignment: alignmentRaw,
    mirrored,
  } = params;

  const alignment = normalizeAlignment(alignmentRaw);

  const distFromStart = placementPoint
    .clone()
    .sub(originalEdgeStart)
    .dot(edgeDir);
  const distFromEnd = originalEdgeLength - distFromStart;

  if (count > 1) {
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return null;
    }
    return (distFromStart - originalEdgeLength * n - padding * denom) / denom;
  }

  if (alignment === Alignment.CENTER) {
    const originalCenter = originalEdgeStart
      .clone()
      .add(originalEdgeEnd)
      .multiplyScalar(0.5);
    const distFromCenterVec = placementPoint.clone().sub(originalCenter);
    return distFromCenterVec.dot(edgeDir) * (mirrored ? -1 : 1);
  }

  const anchorAtStartEdge =
    (alignment === Alignment.START && !mirrored) ||
    (alignment === Alignment.END && mirrored);
  if (anchorAtStartEdge) {
    return distFromStart - padding;
  }
  return distFromEnd - padding;
}

/** @deprecated Use {@link marginFromConnectorPlacement}. */
export function marginFromConnectorWorldPosition(
  placementPoint: THREE.Vector3,
  params: Omit<ConnectorMarginParams, "mirrored"> & { mirrored?: boolean },
): number | null {
  return marginFromConnectorPlacement(placementPoint, {
    ...params,
    mirrored: params.mirrored ?? false,
  });
}

function connectorPaddingFromOption(
  option: Pick<ConnectorSetInfos, "partSelection">,
): number {
  const partSelection = option.partSelection as { cut?: number } | undefined;
  return (partSelection?.cut ?? 0) / 2;
}

function marginParamsFromContext(
  ctx: Record<string, unknown>,
  option: ConnectorMarginDragOption,
): ConnectorMarginParams {
  return {
    originalEdgeStart: new THREE.Vector3(
      ...(ctx.originalEdgeStartPos as [number, number, number]),
    ),
    originalEdgeEnd: new THREE.Vector3(
      ...(ctx.originalEdgeEndPos as [number, number, number]),
    ),
    originalEdgeLength: ctx.originalEdgeLength as number,
    edgeDir: new THREE.Vector3(
      ...(ctx.edgeDirection as [number, number, number]),
    ),
    count: option.count,
    n: (ctx.n as number) ?? 0,
    padding: connectorPaddingFromOption(option),
    alignment: option.alignment,
    mirrored: (ctx.mirrored as boolean) ?? false,
  };
}

/**
 * Margin range implied by edge drag bounds (padded segment endpoints).
 */
export function computeConnectorMarginRangeFromDragContext(
  constraint: Pick<DragConstraint, "type" | "bounds">,
  ctx: Record<string, unknown>,
  option: ConnectorMarginDragOption,
): ConnectorMarginAxisRange | null {
  if (constraint.type !== "edge") {
    return null;
  }

  const { min: bMin, max: bMax } = constraint.bounds;
  const p0 = new THREE.Vector3(bMin[0], bMin[1], bMin[2]);
  const p1 = new THREE.Vector3(bMax[0], bMax[1], bMax[2]);
  const params = marginParamsFromContext(ctx, option);

  const m0 = marginFromConnectorPlacement(p0, params);
  const m1 = marginFromConnectorPlacement(p1, params);
  if (m0 === null || m1 === null) {
    return null;
  }
  return {
    min: Math.min(m0, m1),
    max: Math.max(m0, m1),
  };
}

/**
 * Margin range implied by edge drag bounds (padded segment endpoints) for one 3D part.
 */
export function computeConnectorMarginRangeForEdgePart(
  part: AbstractPartDefinition<string>,
  option: ConnectorMarginDragOption,
): ConnectorMarginAxisRange | null {
  const constraint = part.dragConstraint;
  const ctx = part.settingsContext;
  if (!constraint || !ctx) {
    return null;
  }
  return computeConnectorMarginRangeFromDragContext(constraint, ctx, option);
}

/** Intersection of per-part ranges (same shared `margin` must satisfy every draggable instance). */
export function intersectConnectorMarginRanges(
  ranges: readonly ConnectorMarginAxisRange[],
): ConnectorMarginAxisRange | null {
  if (ranges.length === 0) {
    return null;
  }
  const first = ranges[0];
  if (!first) {
    return null;
  }
  let lo = first.min;
  let hi = first.max;
  for (let i = 1; i < ranges.length; i++) {
    const r = ranges[i];
    if (!r) continue;
    lo = Math.max(lo, r.min);
    hi = Math.min(hi, r.max);
  }
  if (lo > hi) {
    return null;
  }
  return { min: lo, max: hi };
}
